/** Same rules as generateUserPassword in user-admin-contract. The screen builds the value so complexity applies in bulk. */
import { PASSWORD_ADJECTIVES, PASSWORD_NOUNS } from './password-words.ts';
import { PASSWORD_FALLBACK_MIN_LENGTH } from './password-hints.ts';
export interface PasswordComplexity {
  length?: number;
  upper?: boolean;
  lower?: boolean;
  digits?: boolean;
  special?: boolean;
  skipAmbiguous?: boolean;
}

export function generatePassword(lengthOrOptions: number | PasswordComplexity = 16): string {
  const options: PasswordComplexity = typeof lengthOrOptions === 'number' ? { length: lengthOrOptions } : lengthOrOptions;
  const skip = options.skipAmbiguous !== false;
  const chosen = [
    options.upper !== false ? (skip ? 'ABCDEFGHJKLMNPQRSTUVWXYZ' : 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') : '',
    options.lower !== false ? (skip ? 'abcdefghjkmnpqrstuvwxyz' : 'abcdefghijklmnopqrstuvwxyz') : '',
    options.digits !== false ? (skip ? '23456789' : '0123456789') : '',
    options.special !== false ? '!@#$%&*?' : '',
  ].filter((set) => set.length > 0);
  const classes = chosen.length > 0
    ? chosen
    : ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghjkmnpqrstuvwxyz', '23456789', '!@#$%&*?'];
  const pool = classes.join('');
  const requested = Number.isFinite(options.length) ? Math.floor(options.length ?? 16) : 16;
  const size = requested >= 8 ? Math.min(64, requested) : 16;
  const rand = new Uint32Array(size + classes.length);
  crypto.getRandomValues(rand);
  const pick = (alphabet: string, i: number) => alphabet[rand[i]! % alphabet.length]!;
  const chars = classes.map((alphabet, index) => pick(alphabet, index));
  let cursor = classes.length;
  while (chars.length < size) chars.push(pick(pool, cursor++));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rand[i]! % (i + 1);
    const tmp = chars[i]!;
    chars[i] = chars[j]!;
    chars[j] = tmp;
  }
  return chars.join('');
}

/** Jak se heslo vybírá v dialogu: snadné ze slov, silné náhodné, nebo ručně. */
export type PasswordMode = 'easy' | 'strong' | 'manual';

function randomIndex(size: number): number {
  const value = new Uint32Array(1);
  crypto.getRandomValues(value);
  return value[0]! % size;
}

/**
 * Zapamatovatelné heslo: Jahoda-modra-47, při třech slovech Jahoda-modra-kolo-47.
 * Velké první písmeno, pomlčka (na české klávesnici bez Shiftu) a dvě číslice 2–9
 * splní složitost AD (velké, malé, číslice, znak). Bez 0 a 1, ať se nepletou s O a l.
 */
export function generateMemorablePassword(words: 2 | 3 = 3): string {
  const noun = () => PASSWORD_NOUNS[randomIndex(PASSWORD_NOUNS.length)]!;
  const adjective = PASSWORD_ADJECTIVES[randomIndex(PASSWORD_ADJECTIVES.length)]!;
  const first = noun();
  const parts = [first.charAt(0).toUpperCase() + first.slice(1), adjective];
  if (words === 3) parts.push(noun());
  const digits = '23456789';
  parts.push(digits[randomIndex(digits.length)]! + digits[randomIndex(digits.length)]!);
  return parts.join('-');
}

export interface PasswordMakeOptions {
  words?: 2 | 3;
  complexity?: PasswordComplexity;
}

/** Heslo podle zvoleného režimu. U `manual` nic negeneruje. */
export function makePassword(mode: PasswordMode, options: PasswordMakeOptions = {}): string {
  if (mode === 'easy') return generateMemorablePassword(options.words ?? 3);
  if (mode === 'strong') return generatePassword(options.complexity ?? { length: 16 });
  return '';
}

export type PasswordProblem = 'length' | 'complexity' | 'contains_login' | 'contains_name';


/**
 * Okamžitá kontrola ručně zadaného hesla podle pravidel složitosti AD: aspoň `minLength`
 * znaků (výchozí náhradní minimum 8; skutečné minimum dává politika adresáře, viz password-hints), tři ze čtyř skupin (velká, malá, číslice, ostatní) a bez loginu
 * a částí jména od tří znaků (AD dělí jméno na , . - _ mezeře a #).
 */
export function checkPassword(
  password: string,
  context: { login?: string; displayName?: string; minLength?: number } = {},
): PasswordProblem[] {
  const problems: PasswordProblem[] = [];
  if (password.length < (context.minLength ?? PASSWORD_FALLBACK_MIN_LENGTH)) problems.push('length');
  const groups = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(password)).length;
  if (groups < 3) problems.push('complexity');
  const lower = password.toLowerCase();
  const login = (context.login ?? '').trim().toLowerCase();
  if (login.length >= 3 && lower.includes(login)) problems.push('contains_login');
  const tokens = (context.displayName ?? '').toLowerCase().split(/[,.\-_\s#\t]+/).filter((token) => token.length >= 3);
  if (tokens.some((token) => lower.includes(token))) problems.push('contains_name');
  return problems;
}
